import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import type { AdminUserDetail, AdminUserPatch } from "../api/types";
import { ADMIN_CREATE_ROLES, roleLabel } from "../lib/admin";

export function UserDetailPage() {
  const { id } = useParams();
  const userId = Number(id);

  const userQ = useQuery({
    queryKey: ["admin-user", userId],
    queryFn: () => api.admin.get(userId),
    enabled: Number.isInteger(userId),
  });

  if (userQ.isLoading) {
    return (
      <Layout title="User">
        <p className="sub">Loading…</p>
      </Layout>
    );
  }
  if (userQ.isError || !userQ.data) {
    return (
      <Layout title="User">
        <section className="card">
          <p>That user could not be found.</p>
          <Link to="/admin" className="btn btn-ghost btn-xl">
            Back to Admin
          </Link>
        </section>
      </Layout>
    );
  }

  return <UserDetailView user={userQ.data.user} />;
}

function UserDetailView({ user }: { user: AdminUserDetail }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const isSelf = currentUser?.id === user.id;
  const isManagement = user.role === "management";

  const [fullName, setFullName] = useState(user.fullName);
  const [phone, setPhone] = useState(user.phone ?? "");
  const [role, setRole] = useState(user.role);
  const [status, setStatus] = useState(user.status);
  const [buildingIds, setBuildingIds] = useState<number[]>(user.buildingIds);
  const [saved, setSaved] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [pwMessage, setPwMessage] = useState<string | null>(null);

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });

  function toggleBuilding(bid: number) {
    setBuildingIds((ids) => (ids.includes(bid) ? ids.filter((x) => x !== bid) : [...ids, bid]));
  }

  const saveMut = useMutation({
    mutationFn: () => {
      const patch: AdminUserPatch = {
        fullName: fullName.trim(),
        phone: phone.trim() || null,
        status,
      };
      if (!isManagement) patch.role = role;
      if (role !== "maintenance") patch.buildingIds = buildingIds;
      return api.admin.update(user.id, patch);
    },
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["admin-user", user.id] });
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
  });

  const pwMut = useMutation({
    mutationFn: () => api.admin.resetPassword(user.id, newPassword),
    onSuccess: () => {
      setPwMessage("Password reset. Share the new password with the user.");
      setNewPassword("");
    },
    onError: () => setPwMessage("Could not reset the password. Please try again."),
  });

  return (
    <Layout title="Edit User">
      <section className="card form-card">
        <div className="detail-head">
          <div>
            <h2>{user.fullName}</h2>
            <p className="sub">{user.email}</p>
          </div>
          <span className={"badge badge-" + (user.status === "active" ? "ok" : "muted")}>
            {user.status === "active" ? "Active" : "Inactive"}
          </span>
        </div>

        <label className="field">
          <span>Full name</span>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </label>

        <div className="two-col">
          <label className="field">
            <span>Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </label>
          <label className="field">
            <span>Role</span>
            {isManagement ? (
              <input value="Management" disabled />
            ) : (
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                {ADMIN_CREATE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            )}
          </label>
        </div>

        <label className="field">
          <span>Account status</span>
          {isSelf ? (
            <input value="Active (you cannot deactivate your own account)" disabled />
          ) : (
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive — cannot log in</option>
            </select>
          )}
        </label>

        {role !== "maintenance" && (
          <div className="field">
            <span>Building access</span>
            <fieldset className="choice-set">
              {(buildingsQ.data?.buildings ?? []).map((b) => (
                <label key={b.id} className="choice">
                  <input
                    type="checkbox"
                    checked={buildingIds.includes(b.id)}
                    onChange={() => toggleBuilding(b.id)}
                  />
                  <span>{b.name}</span>
                </label>
              ))}
            </fieldset>
          </div>
        )}

        <div className="form-actions">
          {saved && !saveMut.isPending && <span className="saved-note">Saved.</span>}
          {saveMut.isError && <span className="field-error">Could not save. Try again.</span>}
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/admin")}
            disabled={saveMut.isPending}
          >
            Back to list
          </button>
          <button
            className="btn btn-primary btn-xl"
            onClick={() => {
              setSaved(false);
              saveMut.mutate();
            }}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </section>

      <section className="card form-card">
        <h2>Reset password</h2>
        <p className="sub" style={{ marginBottom: 12 }}>
          Set a new password for {user.fullName}, then share it through your approved process.
        </p>
        <label className="field">
          <span>New password</span>
          <input
            type="text"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
          />
        </label>
        {pwMessage && (
          <p className={pwMut.isError ? "field-error" : "saved-note"}>{pwMessage}</p>
        )}
        <div className="form-actions">
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => {
              setPwMessage(null);
              pwMut.mutate();
            }}
            disabled={pwMut.isPending || newPassword.length < 8}
          >
            {pwMut.isPending ? "Resetting…" : "Reset password"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
